"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettlementComponent = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const FilterContext_1 = require("../../context/filters/FilterContext");
const react_router_dom_1 = require("react-router-dom");
const react_1 = require("react");
const useDebounce_1 = require("../../hooks/debounce/useDebounce");
const useSettlement_1 = require("./context/useSettlement");
const usePagination_1 = require("../../hooks/pagination/usePagination");
const clubInvoice_module_scss_1 = __importDefault(require("../club-invoice/clubInvoice.module.scss"));
const table_1 = require("../../components/web-view/table");
const FiltersComponent_1 = require("../../components/filter/FiltersComponent");
const button_1 = require("primereact/button");
const react_i18next_1 = require("react-i18next");
const SettlementMobile_1 = require("./mobile/SettlementMobile");
const useIsMobile_1 = require("../../hooks/mobile/useIsMobile");
const datesHelpers_1 = require("../../common/utils/datesHelpers");
const dateHelpers_1 = require("../../components/filter/dateHelpers");
const useClubMachineFilter_1 = require("../../hooks/filters/useClubMachineFilter");
const FilterButton_1 = require("../../components/filter/filter-button/FilterButton");
const ActiveFilterChips_1 = require("../../components/chip/ActiveFilterChips");
const MobileFilterModal_1 = require("../../components/filter/filter-button/MobileFilterModal");
const MobilePillFilter_1 = require("../../components/filter/filter-button/MobilePillFilter");
const MobileListActions_1 = require("../../components/mobile/MobileListActions");
const SortOptionModal_1 = require("../orders/mobile/SortOptionModal");
const SettlementComponent = () => {
    const { filters, setFilters } = (0, FilterContext_1.useFilters)();
    const { t } = (0, react_i18next_1.useTranslation)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const [isFirstLoad, setIsFirstLoad] = (0, react_1.useState)(true);
    const [globalFilterValue, setGlobalFilterValue] = (0, react_1.useState)(filters.globalFilterValue || '');
    const debouncedFilterValue = (0, useDebounce_1.useDebounce)(globalFilterValue, 500);
    const [currentPage, setCurrentPage] = (0, react_1.useState)(() => {
        const storedPage = localStorage.getItem('currentPage');
        return storedPage ? parseInt(storedPage, 10) : 1;
    });
    const localStorageFilters = JSON.parse(localStorage.getItem('filters') || '{}');
    const [selectedPeriod, setSelectedPeriod] = (0, react_1.useState)({
        month: localStorageFilters.month || '',
        year: localStorageFilters.year || new Date().getFullYear(),
    });
    const [dateFilter, setDateFilter] = (0, react_1.useState)(localStorageFilters.startDate && localStorageFilters.endDate
        ? [
            new Date(localStorageFilters.startDate),
            new Date(localStorageFilters.endDate),
        ]
        : datesHelpers_1.getDefaultDateRangeArray);
    const isMobile = (0, useIsMobile_1.useIsMobile)();
    const [sortField, setSortField] = (0, react_1.useState)('issueDate');
    const [sortOrder, setSortOrder] = (0, react_1.useState)(-1);
    const [showSortModal, setShowSortModal] = (0, react_1.useState)(false);
    const sortOptions = (0, react_1.useMemo)(() => [
        { field: 'issueDate', label: t('date') },
        { field: 'settlementNumber', label: t('invoiceNumber') },
        { field: 'machineName', label: t('machine') },
        { field: 'clubName', label: t('club') },
        { field: 'netPayout', label: t('amount') },
    ], [t]);
    const { getAll } = (0, useSettlement_1.useSettlement)();
    const { tableFilters, displayFilters, filterFields, getFilterFields, optionsMap, handleTableFilterChange, handleRemoveFilter, handleClearAllFilters, activeFilterCount, displayActiveFilterCount, showMobileFilters, setShowMobileFilters, } = (0, useClubMachineFilter_1.useClubMachineFilter)({
        storageKey: 'settlement',
        onFilterChange: () => setCurrentPage(1),
    });
    const tableFiltersRef = (0, react_1.useRef)(tableFilters);
    (0, react_1.useEffect)(() => {
        tableFiltersRef.current = tableFilters;
    }, [tableFilters]);
    const fetchSettlements = async (page, pageSize, filter) => {
        const fromDate = filters.startDate
            ? Math.floor(new Date(filters.startDate).getTime() / 1000).toString()
            : undefined;
        const toDate = filters.endDate
            ? Math.floor(new Date(filters.endDate).getTime() / 1000).toString()
            : undefined;
        const sort = [
            {
                field: sortField,
                direction: sortOrder === 1 ? 'asc' : 'desc',
            },
        ];
        if (fromDate && toDate)
            try {
                return await getAll(page, pageSize, filter, fromDate, toDate, sort, tableFiltersRef.current.machines || [], tableFiltersRef.current.clubs || []);
            }
            catch (error) {
                console.error('Error fetching settlements:', error);
            }
    };
    const { data, loading, fetchData } = (0, usePagination_1.usePagination)(parseInt(localStorage.getItem('currentPage') || '1', 10), 10, (page, pageSize, debounceFilterValue) => fetchSettlements(page, pageSize, debounceFilterValue || ''), (item) => item.id);
    (0, react_1.useEffect)(() => {
        if (isFirstLoad) {
            setIsFirstLoad(false);
            return;
        }
        if (!filters.startDate || !filters.endDate)
            return;
        fetchData(currentPage, 10, false, debouncedFilterValue, tableFilters.machines || [], tableFilters.clubs || [], filters.startDate, filters.endDate, selectedPeriod.year, selectedPeriod.month);
    }, [
        filters.startDate,
        filters.endDate,
        debouncedFilterValue,
        selectedPeriod.month,
        selectedPeriod.year,
        currentPage,
        isFirstLoad,
        tableFilters,
        sortField,
        sortOrder,
    ]);
    // Actualización de filtros globales con debounce
    (0, react_1.useEffect)(() => {
        if (debouncedFilterValue !== undefined) {
            setFilters((prevFilters) => ({
                ...prevFilters,
                globalFilterValue: debouncedFilterValue,
            }));
        }
    }, [debouncedFilterValue]);
    const columns = [
        {
            field: 'issueDate',
            header: 'date',
            renderFunc: (item) => {
                if (!item.billingPeriod)
                    return '';
                return ((0, jsx_runtime_1.jsxs)("span", { children: [item.billingPeriod.startDate, " - ", item.billingPeriod.endDate] }));
            },
        },
        {
            field: 'settlementNumber',
            header: 'invoiceNumber',
            renderFunc: (item) => {
                if (!item.settlementNumber)
                    return '';
                return (0, jsx_runtime_1.jsx)("span", { children: item.settlementNumber });
            },
        },
        {
            field: 'machineName',
            header: 'machine',
            renderFunc: (item) => {
                if (!item.machineName)
                    return '';
                return (0, jsx_runtime_1.jsx)("span", { children: item.machineName });
            },
        },
        {
            field: 'clubName',
            header: 'club',
            renderFunc: (item) => {
                if (!item.clubName)
                    return '';
                return (0, jsx_runtime_1.jsx)("span", { children: item.clubName });
            },
        },
        {
            field: 'netPayout',
            header: 'amount',
            renderFunc: (item) => {
                if (!item.netPayout)
                    return '';
                return (0, jsx_runtime_1.jsxs)("span", { children: [item.netPayout.toFixed(2), " EUR"] });
            },
        },
    ];
    const onGlobalFilterChange = (e) => {
        const getFilters = JSON.parse(localStorage.getItem('filters') || '{}');
        const newFilters = {
            ...getFilters,
            globalFilterValue: e,
            clubId: filters.clubId,
        };
        // Actualiza solo el estado local para debounce
        setGlobalFilterValue(e);
        setCurrentPage(1); // Reinicia la paginación
        localStorage.setItem('currentPage', '1');
        localStorage.setItem('filters', JSON.stringify(newFilters));
    };
    const handlerDateFilter = (dates) => {
        const newFilters = {
            globalFilterValue,
            startDate: dates?.[0]?.toISOString() || null,
            endDate: dates?.[1]?.toISOString() || null,
            clubId: null,
        };
        setFilters(newFilters);
        setDateFilter(dates);
        localStorage.setItem('filters', JSON.stringify(newFilters));
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        localStorage.setItem('currentPage', page.toString());
    };
    const handleSortChange = (field, order) => {
        setSortField(field);
        setSortOrder(order);
        setCurrentPage(1);
        localStorage.setItem('currentPage', '1');
    };
    const handlerGotoDetail = (cil) => {
        navigate(`/settlement/${cil.id}`);
    };
    const fetchMoreData = (page, pageSize, append = false) => {
        fetchData(page, pageSize, append);
        setCurrentPage(page);
        localStorage.setItem('currentPage', page.toString());
    };
    const renderActionButtons = (cil) => ((0, jsx_runtime_1.jsx)("div", { className: "flex justify-center gap-2", children: (0, jsx_runtime_1.jsx)(button_1.Button, { icon: "pi pi-eye", className: "p-button-rounded p-button-info p-button-outlined", onClick: () => handlerGotoDetail(cil) }) }));
    const quickFilters = [
        {
            id: 'lastMonth',
            label: t('lastMonth'),
            range: dateHelpers_1.getLastMonthRange,
        },
        {
            id: 'thisQuarter',
            label: t('thisQuarter'),
            range: dateHelpers_1.getCurrentQuarterRange,
        },
        {
            id: 'thisYear',
            label: t('thisYear'),
            range: dateHelpers_1.getCurrentYearRange,
        },
    ];
    if (isMobile) {
        return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(MobileFilterModal_1.MobileFiltersModal, { visible: showMobileFilters, onHide: () => setShowMobileFilters(false), filters: getFilterFields, values: tableFilters, onFilterChange: handleTableFilterChange, activeCount: activeFilterCount }), (0, jsx_runtime_1.jsx)(SortOptionModal_1.SortOptionsModal, { visible: showSortModal, onHide: () => setShowSortModal(false), sortField: sortField, sortOrder: sortOrder, handleSortChange: handleSortChange, options: sortOptions }), (0, jsx_runtime_1.jsx)(MobilePillFilter_1.MobilePillFilter, { filters: displayFilters, filterFields: filterFields, onRemoveFilter: handleRemoveFilter, onClearAll: handleClearAllFilters }), (0, jsx_runtime_1.jsx)(SettlementMobile_1.SettlementListMobile, { settlements: data.items, loading: loading, pagination: {
                        currentPage,
                        totalPages: Math.ceil(data.pagination.totalPages / 10),
                        pageSize: 10,
                    }, fetchData: fetchMoreData, globalFilterValue: globalFilterValue, onGlobalFilterChange: onGlobalFilterChange, handlerDateFilter: handlerDateFilter, dateFilter: dateFilter, selectedPeriod: selectedPeriod, quickFilters: quickFilters }), (0, jsx_runtime_1.jsx)(MobileListActions_1.MobileListActions, { actions: [
                        {
                            key: 'filter',
                            icon: 'pi pi-filter',
                            label: t('filter'),
                            onClick: () => setShowMobileFilters(true),
                            badge: displayActiveFilterCount,
                        },
                        {
                            key: 'sort',
                            icon: 'pi pi-sort-alt',
                            label: t('sort'),
                            onClick: () => setShowSortModal(true),
                        },
                    ] })] }));
    }
    return ((0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)("div", { className: clubInvoice_module_scss_1.default.container, children: (0, jsx_runtime_1.jsx)(table_1.AppTable, { columns: columns, globalFilterFields: ['machineId'], actions: renderActionButtons, data: data, fetchData: fetchData, loading: loading, getKey: (item) => item.id, currentPage: currentPage, handleCurrentPage: handlePageChange, reduceHeight: true, enableSorting: true, sortableFields: [
                    'issueDate',
                    'settlementNumber',
                    'machineName',
                    'clubName',
                    'netPayout',
                ], sortField: sortField, sortOrder: sortOrder, defaultSort: 'issueDate', onSortChange: handleSortChange, headerContent: (0, jsx_runtime_1.jsx)("div", { className: 'flex items-center gap-2', children: (0, jsx_runtime_1.jsx)("h2", { children: t('settlementList') }) }), filtersComponent: (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-3", children: [(0, jsx_runtime_1.jsx)(FilterButton_1.FilterButton, { filters: getFilterFields, values: tableFilters, onFilterChange: handleTableFilterChange, buttonLabel: t('') }), (0, jsx_runtime_1.jsx)(FiltersComponent_1.FiltersComponent, { globalFilterValue: globalFilterValue, onGlobalFilterChange: onGlobalFilterChange, handlerDateFilter: handlerDateFilter, resetPageToFirst: () => setCurrentPage(1), inputFilterActive: true, quickFilters: quickFilters })] }), activeFiltersComponent: (0, jsx_runtime_1.jsx)(ActiveFilterChips_1.ActiveFilterChips, { filters: displayFilters, optionsMap: optionsMap, onRemoveFilter: handleRemoveFilter }) }) }) }));
};
exports.SettlementComponent = SettlementComponent;
